import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Chip,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  AccessTime,
  ExitToApp,
  Refresh,
  CalendarToday,
  Schedule,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import { format, parseISO, startOfMonth, endOfMonth } from 'date-fns';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Attendance = () => {
  const { user } = useAuth();
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [monthlyRecords, setMonthlyRecords] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState({
  totalDays: 0,
  presentDays: 0,
  lateDays: 0,
  absentDays: 0,
  totalHours: 0,
  averageHours: 0
});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('today');
  const [selectedMonth, setSelectedMonth] = useState(new Date());
// Helper function to safely format hours
const formatHours = (hours) => {
  if (!hours && hours !== 0) return '0.00';
  const numHours = typeof hours === 'number' ? hours : parseFloat(hours);
  return isNaN(numHours) ? '0.00' : numHours.toFixed(2);
};


  const isAdmin = user?.roles?.includes('admin') || user?.roles?.includes('super_madmin');

  useEffect(() => {
    fetchAttendanceData();
  }, [selectedMonth]);



const fetchAttendanceData = async () => {
  try {
    setLoading(true);
    
    // Fetch today's attendance
    try {
      const todayResponse = await api.get('/attendance/today');
      setTodayAttendance(todayResponse.data);
    } catch (error) {
      console.error('Error fetching today attendance:', error);
      setTodayAttendance(null);
    }

    // Fetch monthly records
    const startDate = format(startOfMonth(selectedMonth), 'yyyy-MM-dd');
    const endDate = format(endOfMonth(selectedMonth), 'yyyy-MM-dd');
    
    try {
      const monthlyResponse = await api.get('/attendance/report', {
        params: {
          start_date: startDate,
          end_date: endDate
        }
      });
      
      console.log('Monthly attendance response:', monthlyResponse.data);
      
      // Handle different response formats
      let records = [];
      if (Array.isArray(monthlyResponse.data)) {
        // Non-admin response (simple array)
        records = monthlyResponse.data;
      } else if (monthlyResponse.data && monthlyResponse.data.attendance) {
        // Admin response (object with attendance array)
        records = monthlyResponse.data.attendance;
      }
      
      setMonthlyRecords(records || []);
      calculateMonthlyStats(records || []);
    } catch (error) {
      console.error('Error fetching monthly attendance:', error);
      setMonthlyRecords([]);
      calculateMonthlyStats([]);
    }
  } catch (error) {
    console.error('Error in fetchAttendanceData:', error);
    setMonthlyRecords([]);
    calculateMonthlyStats([]);
  } finally {
    setLoading(false);
  }
};




  
const calculateMonthlyStats = (records) => {
  // Always set stats, even if records is invalid
  if (!Array.isArray(records) || records.length === 0) {
    setMonthlyStats({
      totalDays: 0,
      presentDays: 0,
      lateDays: 0,
      absentDays: 0,
      totalHours: 0,
      averageHours: 0
    });
    return;
  }
  
  const stats = {
    totalDays: records.length,
    presentDays: records.filter(r => r.status === 'present').length,
    lateDays: records.filter(r => r.status === 'late').length,
    absentDays: records.filter(r => r.status === 'absent').length,
    totalHours: records.reduce((sum, r) => sum + (parseFloat(r.total_hours) || 0), 0),
    averageHours: 0
  };
  
  if (stats.presentDays > 0) {
    stats.averageHours = (stats.totalHours / stats.presentDays).toFixed(2);
  }
  
  setMonthlyStats(stats);
};


  const handleClockIn = async () => {
    try {
      await api.post('/attendance/clock-in', {
        location: 'Office' // You can make this dynamic
      });
      fetchAttendanceData();
    } catch (error) {
      console.error('Error clocking in:', error);
    }
  };

  const handleClockOut = async () => {
    try {
      await api.post('/attendance/clock-out', {
        notes: ''
      });
      fetchAttendanceData();
    } catch (error) {
      console.error('Error clocking out:', error);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const isClockedIn = todayAttendance && todayAttendance.clock_in && !todayAttendance.clock_out;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        My Attendance
      </Typography>

      {/* Today's Status Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Today's Status
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                

{isClockedIn ? (
  <>
    <CheckCircle color="success" />
    <Typography>
      Clocked in at {format(parseISO(todayAttendance.clock_in), 'HH:mm')}
    </Typography>
  </>
) : todayAttendance?.clock_out ? (
  <>
    <CheckCircle color="info" />
    <Typography>
      Worked {formatHours(todayAttendance.total_hours)} hours today
    </Typography>
  </>
) : (
  <>
    <Cancel color="error" />
    <Typography>Not clocked in yet</Typography>
  </>
)}

              </Box>
            </Grid>
            <Grid item xs={12} md={6} textAlign="right">
              {!todayAttendance || !todayAttendance.clock_in ? (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AccessTime />}
                  onClick={handleClockIn}
                  size="large"
                >
                  Clock In
                </Button>
              ) : !todayAttendance.clock_out ? (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<ExitToApp />}
                  onClick={handleClockOut}
                  size="large"
                >
                  Clock Out
                </Button>
              ) : null}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Monthly Summary - Only personal data */}
      {monthlyStats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">{monthlyStats.presentDays}</Typography>
                <Typography variant="body2" color="textSecondary">Present Days</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">{monthlyStats.lateDays}</Typography>
                <Typography variant="body2" color="textSecondary">Late Days</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">{monthlyStats.totalHours.toFixed(1)}</Typography>
                <Typography variant="body2" color="textSecondary">Total Hours</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">{monthlyStats.averageHours}</Typography>
                <Typography variant="body2" color="textSecondary">Avg Hours/Day</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Personal Attendance Records */}
      <Paper sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">My Attendance History</Typography>
          <Button startIcon={<Refresh />} onClick={fetchAttendanceData}>
            Refresh
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Clock In</TableCell>
                <TableCell>Clock Out</TableCell>
                <TableCell>Hours</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Location</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {monthlyRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No attendance records found
                  </TableCell>
                </TableRow>
              ) : (
                monthlyRecords.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>{format(parseISO(record.date), 'MMM dd, yyyy')}</TableCell>
                    <TableCell>
                      {record.clock_in ? format(parseISO(record.clock_in), 'HH:mm') : '-'}
                    </TableCell>
                    <TableCell>
                      {record.clock_out ? format(parseISO(record.clock_out), 'HH:mm') : '-'}
                    </TableCell>
                    <TableCell>
                     {formatHours(record.total_hours)}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={record.status} 
                        size="small"
                        color={record.status === 'present' ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>{record.location || '-'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default Attendance;
